import classNames from 'classnames'
import React, { Fragment, memo, useCallback, useEffect, useMemo, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { Input, Spinner } from 'reactstrap'
import { ReactComponent as IconSearch } from '../../../../../assets/icons/search.svg'
import { ReactComponent as Transparent } from '../../../../../assets/icons/transparent.svg'
import { ReactComponent as Trash } from '../../../../../assets/icons/trash-alt.svg'
import EmptyList from '../../../../../assets/icons/empty-list.svg'
import { isBackofficeEnv as checkEnv } from '../../../../../helpers/isBackoffceEnv'
import { deleteDesignLibraryTemplate, getListDesignLibraryTemplate } from '../../../../../services/ArtworkTemplateServices'
import { getLocalData, setLocalData } from '../../../../../services/StorageServices'
import { confirmModal } from '../../../../../shared/PFConfirmModal'
import { toaster } from '../../../../../shared/PFToast'
import { useSelectedVariantContext } from '../../../context/SelectedVariantContext'
import { actionSetSelectedTemplate } from '../../../context/SelectedVariantContext/action'
import { _fetchRegularDesignImage, loadCustomFont } from '../../../context/SelectedVariantContext/helper'

async function imageUrlToBlobUrl(imageUrl) {
    try {
        const response = await fetch(imageUrl, {
            mode: 'cors',
            credentials: 'omit', // Don't send cookies for cross-origin requests
            headers: {
                'Accept': 'image/*'
            }
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const blob = await response.blob();

        // Create a blob URL from the blob
        const blobUrl = URL.createObjectURL(blob);

        return blobUrl;
    } catch (error) {
        throw error;
    }
}

const ModalTemplates = () => {
    const {
        state: { triggerLoadTemplate },
        dispatch,
    } = useSelectedVariantContext()

    const [total, setTotal] = useState(0)
    const [loadingFetch, setLoadingFetch] = useState(true)
    const [loadingAddTemplate, setLoadingAddTemplate] = useState(false)
    const [hasMore, setHasMore] = useState(true)
    const [params, setParams] = useState({
        page: 1,
        limit: 20,
    })

    const [pages, setPages] = useState(0)
    const [listTemplates, setListTemplates] = useState([])
    const designTemplates = getLocalData('designTemplate') || []

    const isBackofficeEnv = checkEnv()

    const localSearchTemplate = useMemo(() => {
        return designTemplates.filter((template) => {
            return template.name.toLowerCase().includes(params.name?.toLowerCase() || '')
        }).sort((a, b) => {
            return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        })
    }, [designTemplates, params.name])

    const _fetchDesignLibrary = useCallback(async () => {
        try {
            setLoadingFetch(true)
            if (isBackofficeEnv) {
                const { success, data, message } = await getListDesignLibraryTemplate({
                    ...params,
                    page: 1,
                })
                if (!success) throw new Error(message)
                const { designTemplate, limit, total } = data
                setTotal(total)
                setPages(Math.ceil(total / limit))
                setHasMore(params.page < Math.ceil(total / limit))
                setListTemplates(designTemplate)
            } else {
                setHasMore(false)
                setListTemplates(localSearchTemplate)
            }
        } catch (error) {
            toaster({
                type: 'error',
                message: error.message,
            })
        } finally {
            setLoadingFetch(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.name, designTemplates])

    useEffect(() => {
        _fetchDesignLibrary()
    }, [params.name, triggerLoadTemplate])

    let _timeOut = null
    const handleSearch = (e) => {
        const { name, value } = e.target
        if (_timeOut) clearTimeout(_timeOut)

        _timeOut = setTimeout(() => {
            setParams((prev) => ({ ...prev, [name]: value ? value.trim() : '', page: 1 }))
        }, 500)
    }

    const handleDeleteTemplate = (id) => {
        const _deleteImage = async () => {
            try {
                if (isBackofficeEnv) {
                    const { success, message } = await deleteDesignLibraryTemplate(id)
                    if (!success) throw new Error(message)
                    setParams({ ...params, page: 1 })
                    _fetchDesignLibrary()
                } else {
                    const _listTemplates = Object.assign([], listTemplates)
                    if (_listTemplates.length > 0) {
                        const availableDesigns = _listTemplates.filter((design) => {
                            return design._id !== id
                        })
                        setLocalData('designTemplate', availableDesigns)
                        setListTemplates(availableDesigns)
                    }
                }
            } catch (error) {
                toaster({
                    type: 'error',
                    message: error.message,
                })
            }
        }

        confirmModal({
            confirmText: 'Delete',
            confirmColor: 'danger',
            title: 'Confirm Delete Template',
            message: 'Are you sure you want to delete this template?',
            onOk: () => _deleteImage(),
        })
    }

    const addTemplate = async (template) => {
        setLoadingAddTemplate(template._id)
        const { layers } = template
        const newLayers = await Promise.all(layers.map(async (item) => {
            if (item.layerType === 'text' && item.linkFont && item.fontFamily) {
                await loadCustomFont(item.fontFamily, item.linkFont)
            }
            if (!!item.image_id) {
                const { origin_url } = await _fetchRegularDesignImage(item.image_id)
                return {
                    ...item,
                    src: origin_url
                }
            }

            if (!!item.src) {
                const blobUrl = await imageUrlToBlobUrl(item.src)
                return {
                    ...item,
                    src: blobUrl,
                }
            } 
            return item
        }))
        template.layers = newLayers
        dispatch(actionSetSelectedTemplate(template))
        setLoadingAddTemplate(false)
    }

    const _next = async () => {
        const newPage = params.page + 1
        try {
            if (newPage > pages) return setHasMore(false)
            const { success, data, message } = await getListDesignLibraryTemplate({ ...params, page: newPage })
            if (!success) throw new Error(message)
            const { designTemplate, pages: responsePages } = data
            setPages(responsePages)
            const concatTemplates = [...listTemplates, ...designTemplate]
            setListTemplates(concatTemplates)
            setHasMore(params.page < Math.ceil(total / params.limit))
        } catch (error) {
            toaster({
                type: 'error',
                message: error.message,
            })
        } finally {
            setParams((prev) => ({ ...prev, page: newPage }))
        }
    }

    return (
        <div className="MockupTemplates">
            <div className="">
                <h3>My template</h3>
                <div className="CboTemplates d-flex align-items-center">
                    <div className='InputSearchTemplates'>
                        <IconSearch />
                        <Input name="name" id='inputSearch' placeholder='Search...' onChange={handleSearch} className='InputSearchTemplates_input' />
                    </div>
                </div>
                <div className='TemplatesList'>
                    {loadingFetch ? (
                        <div className='ListTemplates mt-3'>
                            <Loading />
                        </div>
                    ) : (
                        <Fragment>
                            {listTemplates.length > 0 ? (
                                <Fragment>
                                    {isBackofficeEnv ? (
                                        <InfiniteScroll
                                            next={_next}
                                            hasMore={hasMore}
                                            loader={!loadingFetch && <Loading />}
                                            className='ListTemplates mb-3'
                                            dataLength={listTemplates.length}
                                            height={window.innerHeight - 260}
                                        >
                                            <div className='TemplatesListWrapper'>
                                                {listTemplates.map((template) => {
                                                    return (
                                                        <div key={template._id} className='TemplateItemWrapper'>
                                                            <TemplateItem
                                                                template={template}
                                                                addTemplate={addTemplate}
                                                                handleDeleteTemplate={handleDeleteTemplate}
                                                                loadingAddTemplate={loadingAddTemplate}
                                                            />
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        </InfiniteScroll>
                                    ) : (
                                        <div className="ListTemplates mb-3">
                                            <div className='TemplatesListWrapper'>
                                                {listTemplates.map((template) => {
                                                    return (
                                                        <div key={template._id} className='TemplateItemWrapper'>
                                                            <TemplateItem
                                                                template={template}
                                                                addTemplate={addTemplate}
                                                                handleDeleteTemplate={handleDeleteTemplate}
                                                                loadingAddTemplate={loadingAddTemplate}
                                                            />
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </Fragment>
                            ) : (
                                <div className="NoImage mt-2">
                                    <div className='text-center'>
                                        <img src={EmptyList} alt='Empty' />
                                        <p className='mt-3'>No templates yet.</p>
                                    </div>
                                </div>
                            )}
                        </Fragment>
                    )}
                </div>
            </div>
        </div>
    )
}


const TemplateItem = memo((props) => {
    const { template, addTemplate, handleDeleteTemplate, loadingAddTemplate } = props

    const { _id, preview_image, name } = template

    return (
        <Fragment>
            <div
                key={_id}
                className={classNames('TemplateItem')}
            >
                <div className='position-relative overflow-hidden WrapImage'>
                    {loadingAddTemplate === _id && <Spinner style={{ color: '#007bff' }} className='loading-add-template position-absolute' />}
                    <Transparent width={'100%'} height={'100%'} />
                    <img id="update_image" className="Thumbnail" src={preview_image} alt='thumbnail'
                        onClick={() => addTemplate(template)} onDoubleClick={(event) => event.preventDefault()} />
                </div>
                <div className='Info'>
                    <p className='Title'>{name}</p>
                </div>
                <div
                    className="ButtonDelete cursor-pointer"
                    id="delete_image_library"
                    onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                        handleDeleteTemplate(_id)
                    }}
                >
                    <Trash widths={20} height={20} color="#ffffff" />
                </div>
            </div>
        </Fragment>
    )
})



const Loading = () => {
    return (
        <div className="LoadingScroll">
            <Spinner style={{ color: '#007bff' }} />
        </div>
    )
}

// ModalUploadFont.propsType = {
//     isOpen: PropsType.bool.required
// }

export default ModalTemplates

